/*
 * finishing-display.js
 *
 * Two modes (driven by section's data-finishing-mode):
 *  - 'select'  → multi-color product. Cards are FIXED to specific finishing-color
 *                variant values. Click → dispatches click on the matching hidden
 *                radio in add-to-cart.liquid; existing variant-selector JS handles
 *                the variant change. On variant change, .is-selected moves to the
 *                card whose value matches the now-selected variant's option value.
 *                On touch, the tap ALSO opens the finish sheet (below).
 *  - 'display' → single/no finishing-color variant. ONE read-only card per
 *                product. Variant change repaints image sources + label.
 *                Click/tap (all devices) → finish sheet.
 *
 * Hover (both modes, desktop only): the .ag-finish-panel detail card opens
 * above the card — swatch photo w/ accent strip, name, tone pill + use-case,
 * texture, material. JS swaps its content (paintPanelContent).
 *
 * Finish sheet (2026-07-17 — replaces the legacy .ag-finish-modal): one
 * singleton dialog built at open time on document.body. The interior is a
 * CLONE of the section's .ag-finish-panel (structural parity by construction —
 * the panel's CSS, incl. its ≤767px stacked layout, styles the clone), plus,
 * in SELECT mode, a swatch row cloned from the page cards so the user can
 * switch finishes inside the sheet (each switch drives the real variant
 * radio). ≤767px it presents as a bottom sheet (slide-up, content-hugging,
 * mechanics ported from the sample picker); above that, a centered dialog.
 * Images: the 400px tier paints synchronously (cache-hot — same URL as the
 * card thumb), then a token-guarded background upgrade to the 800px tier.
 */

(function () {
  'use strict';

  // ---------- Finish sheet (module scope — one singleton across sections) ----

  var SHEET_CLOSE_SVG = '<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" focusable="false"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"/></svg>';

  // { backdrop, panel, row, ctx, key, token, trigger, onKey, prevOverflow }
  var sheetState = null;

  // Set while closeSheet() restores focus to the trigger card: the card's
  // focus listener would otherwise pop the desktop hover panel with no mouse
  // over it (latent bug in the legacy modal's focus-restore). focus()
  // dispatches synchronously, so the set/clear bracket is sufficient.
  var suppressFocusPreview = false;

  // Read primary + accent (URL + name + spec strings) from a card's data
  // attributes. Pure — used by the hover panel, and by the sheet (useModal=true
  // reads the 800px data-finish-modal-* tier with 400px fallback).
  function readPair(card, useModal) {
    var hasAccent = card.dataset.hasAccent === 'true';
    var primarySrc, accentSrc;
    if (useModal) {
      primarySrc = card.dataset.finishModalPrimary ||
                   card.dataset.finishLargePrimary ||
                   card.dataset.finishLarge || '';
      accentSrc = hasAccent ? (
        card.dataset.finishModalAccent ||
        card.dataset.finishLargeAccent || ''
      ) : '';
    } else {
      primarySrc = card.dataset.finishLargePrimary || card.dataset.finishLarge || '';
      accentSrc = hasAccent ? (card.dataset.finishLargeAccent || '') : '';
    }
    var primaryName = card.dataset.finishNamePrimary || card.dataset.finishName || '';
    var accentName  = hasAccent ? (card.dataset.finishNameAccent || '') : '';
    return {
      hasAccent: hasAccent && !!accentSrc,
      primaryLarge: primarySrc,
      primaryName: primaryName,
      accentLarge: accentSrc,
      accentName: accentName,
      toneLabel: card.dataset.finishToneLabel || '',
      usecase: card.dataset.finishUsecase || '',
      texture: card.dataset.finishTexture || '',
      material: card.dataset.finishMaterial || '',
      key: card.dataset.variantValue || card.dataset.finishNamePrimary || primarySrc
    };
  }

  // Commit a pair into a .ag-finish-panel root (the hover panel OR the sheet's
  // clone). Images + text land together — atomic, so a swap can never show
  // new text with old pixels. Queries per call; paints are rare.
  function paintPanelContent(root, pair) {
    root.dataset.hasAccent = pair.hasAccent ? 'true' : 'false';
    var primary = root.querySelector('.ag-finish-panel-primary');
    var strip = root.querySelector('.ag-finish-panel-strip');
    var accent = root.querySelector('.ag-finish-panel-accent');
    var name = root.querySelector('.ag-finish-panel-name');
    var accentName = root.querySelector('.ag-finish-panel-accentname');
    var toneRow = root.querySelector('.ag-finish-panel-tone-row');
    var pill = root.querySelector('.ag-finish-panel-pill');
    var usecase = root.querySelector('.ag-finish-panel-usecase');
    var textureRow = root.querySelector('.ag-finish-panel-texture-row');
    var texture = root.querySelector('.ag-finish-panel-texture');
    var material = root.querySelector('.ag-finish-panel-material');
    if (primary) {
      primary.src = pair.primaryLarge;
      primary.alt = pair.primaryName || '';
      primary.style.visibility = '';
    }
    if (strip) strip.hidden = !pair.hasAccent;
    if (accent) {
      if (pair.hasAccent) { accent.src = pair.accentLarge; accent.alt = pair.accentName || ''; }
      else { accent.src = ''; accent.alt = ''; }
    }
    if (name) name.textContent = pair.primaryName || '';
    if (accentName) {
      accentName.textContent = pair.accentName ? ('with ' + pair.accentName + ' accent') : '';
      accentName.hidden = !pair.hasAccent;
    }
    if (toneRow) toneRow.hidden = !pair.toneLabel;
    if (pill) pill.textContent = pair.toneLabel || '';
    if (usecase) usecase.textContent = pair.usecase || '';
    if (textureRow) textureRow.hidden = !pair.texture;
    if (texture) texture.textContent = pair.texture || '';
    if (material) material.textContent = pair.material || '';
  }

  // Stage a pair's images (decode-then-swap discipline), then call commit().
  // Guards live in the caller's commit closure.
  function stagePair(pair, commit, timeoutMs) {
    var pending = (pair.hasAccent && pair.accentLarge) ? 2 : 1;
    var settle = function () { pending -= 1; if (pending <= 0) commit(); };
    var sP = new Image();
    if ('fetchPriority' in sP) sP.fetchPriority = 'high';
    sP.src = pair.primaryLarge;
    if (sP.decode) sP.decode().then(settle, settle);
    else { sP.onload = settle; sP.onerror = settle; }
    if (pair.hasAccent && pair.accentLarge) {
      var sA = new Image();
      if ('fetchPriority' in sA) sA.fetchPriority = 'high';
      sA.src = pair.accentLarge;
      if (sA.decode) sA.decode().then(settle, settle);
      else { sA.onload = settle; sA.onerror = settle; }
    }
    if (timeoutMs) setTimeout(commit, timeoutMs);
  }

  // Background upgrade of the sheet's images from the 400px tier to the 800px
  // tier. Same pixels at higher res → invisible swap; a stalled 800 fetch just
  // never lands (no timeout fallback — the 400 is already showing).
  function stageSheetUpgrade(card) {
    if (!sheetState) return;
    var hi = readPair(card, true);
    var lo = readPair(card);
    if (!hi.primaryLarge || hi.primaryLarge === lo.primaryLarge) return;
    var token = sheetState.token;
    var myKey = sheetState.key;
    var committed = false;
    stagePair(hi, function () {
      if (committed) return;
      if (!sheetState || sheetState.token !== token || sheetState.key !== myKey) return;
      committed = true;
      var pImg = sheetState.panel.querySelector('.ag-finish-panel-primary');
      if (pImg) pImg.src = hi.primaryLarge;
      if (hi.hasAccent && hi.accentLarge) {
        var aImg = sheetState.panel.querySelector('.ag-finish-panel-accent');
        if (aImg) aImg.src = hi.accentLarge;
      }
    });
  }

  // Swap the open sheet's content to another finish (in-sheet swatch tap or an
  // external variant change). Atomic: images decode first, then images + text
  // commit together. No-ops when the key is unchanged (e.g. the radio
  // cascade's 120ms echo after an in-sheet select).
  function repaintSheet(sourceCard) {
    if (!sheetState) return;
    var pair = readPair(sourceCard);
    if (!pair.primaryLarge || sheetState.key === pair.key) return;
    sheetState.key = pair.key;
    var token = ++sheetState.token;
    var committed = false;
    stagePair(pair, function () {
      if (committed) return;
      if (!sheetState || sheetState.token !== token) return;
      committed = true;
      paintPanelContent(sheetState.panel, pair);
      stageSheetUpgrade(sourceCard);
    }, 3000);
  }

  function onSheetKey(e) {
    if (e.key === 'Escape') closeSheet();
  }

  function closeSheet() {
    if (!sheetState) return;
    var s = sheetState;
    sheetState = null; // interaction dead immediately
    document.removeEventListener('keydown', onSheetKey);
    document.body.style.overflow = s.prevOverflow;
    document.body.style.paddingRight = '';
    s.backdrop.classList.remove('is-open');
    s.backdrop.style.pointerEvents = 'none';
    // animate out (slide-down / fade), then remove — 320ms covers both the
    // 0.24s desktop and 0.28s mobile transitions.
    setTimeout(function () { s.backdrop.remove(); }, 320);
    if (s.trigger) {
      suppressFocusPreview = true;
      try { s.trigger.focus({ preventScroll: true }); } catch (err) { s.trigger.focus(); }
      suppressFocusPreview = false;
    }
  }

  function openSheet(ctx, triggerCard) {
    if (!ctx.preview) return;
    var pair = readPair(triggerCard); // 400px tier — cache-hot (== card thumb URL)
    if (!pair.primaryLarge) return;
    if (sheetState) {
      // double-tap / programmatic re-entry: refresh content, never re-open
      if (sheetState.key !== pair.key) repaintSheet(triggerCard);
      return;
    }
    // never layer under/over the sample picker
    if (document.querySelector('.agsp-backdrop')) return;
    // sweep any previous instance still in its 320ms fade-out
    document.querySelectorAll('.ag-finish-sheet-backdrop').forEach(function (n) { n.remove(); });

    var backdrop = document.createElement('div');
    backdrop.className = 'ag-finish-sheet-backdrop';
    backdrop.setAttribute('role', 'dialog');
    backdrop.setAttribute('aria-modal', 'true');
    backdrop.setAttribute('aria-label', 'Finish details');

    var sheetEl = document.createElement('div');
    sheetEl.className = 'ag-finish-sheet';

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ag-finish-sheet-close';
    closeBtn.setAttribute('aria-label', 'Close finish details');
    closeBtn.innerHTML = SHEET_CLOSE_SVG;
    sheetEl.appendChild(closeBtn);

    // Interior: clone the section's live panel. Hygiene — the source node is a
    // hidden tooltip that may carry popover positioning + cardKey state.
    var panel = ctx.preview.cloneNode(true);
    panel.hidden = false;
    panel.removeAttribute('role');
    panel.style.top = '';
    panel.style.left = '';
    panel.style.right = '';
    delete panel.dataset.cardKey;
    sheetEl.appendChild(panel);

    // SELECT mode: swatch row cloned from the page cards (clones carry all
    // data-attrs + .is-selected/.is-disabled state; no listeners copied).
    // Direct child of the sheet, AFTER the panel — a full-width bottom row
    // (A's r3 layout, 2026-07-17: chips span the whole sheet width).
    var row = null;
    if (ctx.mode === 'select' && ctx.cards.length > 1) {
      row = document.createElement('div');
      row.className = 'ag-finish-sheet-row';
      row.setAttribute('role', 'group');
      row.setAttribute('aria-label', 'All finishes');
      ctx.cards.forEach(function (pageCard) {
        if (!pageCard.classList.contains('ag-finish-card--selectable')) return;
        row.appendChild(pageCard.cloneNode(true));
      });
      sheetEl.appendChild(row);
    }

    // $5 sample callout (A, 2026-07-17 r4): clone the page's sample-request
    // link into the sheet, under the finishing selections. Same markup +
    // classes → sample-picker.css styles it identically; clicks FORWARD to
    // the ORIGINAL page link so the sample feature's own handler runs
    // (picker / direct-add / inert no-op) — zero edits to sample-request
    // files. The sheet closes first so the picker / cart drawer opens over
    // the page exactly as it does when tapped there. Renders only when the
    // page link renders (≥1 orderable sample) — same visibility rule.
    var sampleWrap = document.querySelector('.ag-sample-link-wrap');
    if (sampleWrap) {
      var callout = sampleWrap.cloneNode(true);
      var calloutMsg = callout.querySelector('.ag-sample-link-msg');
      if (calloutMsg) { calloutMsg.hidden = true; calloutMsg.textContent = ''; }
      // r5 (A, 2026-07-19): on the DESKTOP dialog the callout lives INSIDE the
      // panel's text column, under MATERIAL — it aligns with the body copy and
      // the empty full-width bottom band disappears (A: "white space at the
      // bottom, misaligned Request-sample link"). Mobile keeps the r4
      // full-width placement under the chips row. Resolved at open time;
      // paintPanelContent only writes named field nodes, so chip/card
      // switches never drop an in-body callout.
      var calloutBody = panel.querySelector('.ag-finish-panel-body');
      var desktopDialog = window.matchMedia && window.matchMedia('(min-width: 768px)').matches;
      ((desktopDialog && calloutBody) ? calloutBody : sheetEl).appendChild(callout);
      callout.addEventListener('click', function (e) {
        var b = e.target && e.target.closest ? e.target.closest('.ag-sample-link') : null;
        if (!b) return;
        e.preventDefault();
        e.stopPropagation();
        var orig = sampleWrap.querySelector('.ag-sample-link');
        closeSheet();
        if (orig) orig.click();
      });
    }

    backdrop.appendChild(sheetEl);
    document.body.appendChild(backdrop);

    document.addEventListener('keydown', onSheetKey);

    sheetState = {
      backdrop: backdrop,
      panel: panel,
      row: row,
      ctx: ctx,
      key: pair.key,
      token: 0,
      trigger: triggerCard,
      prevOverflow: document.body.style.overflow
    };
    // Compensate for the scrollbar width disappearing under overflow:hidden so the
    // page doesn't reflow wider behind the sheet (measured live).
    var fdScrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (fdScrollbarWidth > 0) document.body.style.paddingRight = fdScrollbarWidth + 'px';
    document.body.style.overflow = 'hidden';

    // Synchronous first paint (the 400px tier is decoded — the user just
    // tapped the card displaying it), then background 800px upgrade.
    paintPanelContent(panel, pair);
    stageSheetUpgrade(triggerCard);

    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) closeSheet();
    });
    closeBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      closeSheet();
    });
    if (row) {
      row.addEventListener('click', function (e) {
        var clone = e.target && e.target.closest ? e.target.closest('.ag-finish-card--selectable') : null;
        if (!clone || clone.classList.contains('is-disabled') || !sheetState) return;
        var v = clone.dataset.variantValue;
        // optimistic ring in BOTH the row clones and the page cards (mirrors
        // the page click handler's optimistic toggle)
        Array.prototype.forEach.call(row.querySelectorAll('.ag-finish-card--selectable'), function (c) {
          c.classList.toggle('is-selected', c === clone);
        });
        ctx.cards.forEach(function (c) { c.classList.toggle('is-selected', c.dataset.variantValue === v); });
        var src = ctx.cardByValue(v); // the PAGE card — canonical data source
        if (src) repaintSheet(src);
        ctx.clickRadioForValue(v); // real variant change → price/gallery
      });
    }

    try { closeBtn.focus({ preventScroll: true }); } catch (err) { closeBtn.focus(); }

    // entrance: class lands one frame after mount so the transition runs.
    // Guarded: on <=991px customizable PDPs agcf.js (a8LiveDismiss) closes the
    // sheet in the same tick as the tap; adding is-open after closeSheet() had
    // already run faded the backdrop in for the whole 320ms removal window
    // (mobile sheet flash, surfaced 2026-09-08).
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (sheetState && sheetState.backdrop === backdrop) backdrop.classList.add('is-open');
      });
    });
  }

  function initSection(sectionEl) {
    var mode = sectionEl.dataset.finishingMode || 'display';
    var productId = sectionEl.dataset.productId;
    var optionName = sectionEl.dataset.finishingOptionName || '';
    var dataNode = document.getElementById('finishing-data-' + productId);
    if (!dataNode) return;

    var dataMap;
    try {
      dataMap = JSON.parse(dataNode.textContent);
    } catch (err) {
      return;
    }

    var cards = sectionEl.querySelectorAll('.ag-finish-card');
    // DISPLAY-mode card: a single read-only .ag-finish-card--display button.
    // SELECT-mode cards: many .ag-finish-card--selectable buttons.
    var displayCard = sectionEl.querySelector('.ag-finish-card--display');
    var hoverables = sectionEl.querySelectorAll('.ag-finish-card');
    var preview = sectionEl.querySelector('.ag-finish-panel');
    var previewPrimary = preview && preview.querySelector('.ag-finish-panel-primary');
    var hoverSupported = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    var previewToken = 0;

    // Context bundle handed to the module-scope finish sheet.
    // clickRadioForValue is a hoisted function declaration below.
    var ctx = {
      mode: mode,
      sectionEl: sectionEl,
      preview: preview,
      cards: cards,
      cardByValue: function (v) {
        for (var i = 0; i < cards.length; i++) {
          if (cards[i].dataset.variantValue === String(v)) return cards[i];
        }
        return null;
      },
      clickRadioForValue: function (v) { return clickRadioForValue(v); }
    };

    // ---------- DISPLAY MODE: re-paint the single card on variant change ----

    // Repaint the DISPLAY-mode card's primary + accent images, label, and
    // data attributes from the current variant's metaobject data. Hides the
    // entire card if BOTH primary AND accent are incomplete on the new variant.
    // If accent goes from present → missing, flips data-has-accent to "false".
    // Accent-only anomaly: if primary missing but accent present, promote
    // accent into the primary slot (matches Liquid-side disp_primary logic).
    function paintDisplayCard(card, primary, accent) {
      var primaryComplete = !!(primary && primary.thumb && primary.name);
      var accentComplete = !!(accent && accent.thumb && accent.name);
      if (!primaryComplete && !accentComplete) {
        card.hidden = true;
        return;
      }
      // Accent-only promotion
      if (!primaryComplete && accentComplete) {
        primary = accent;
        accent = null;
        primaryComplete = true;
        accentComplete = false;
      }
      card.hidden = false;
      var hasAccent = accentComplete;

      var wrap = card.querySelector('.ag-finish-card-img-wrap');
      var primaryImg = card.querySelector('.ag-finish-card-img--primary');
      var accentImg = card.querySelector('.ag-finish-card-img--accent');
      var nameEl = card.querySelector('.ag-finish-card-name');

      // a11y 1d (2026-10-22): the card names the finish (its aria-label and .ag-finish-card-name), so its pictures stay
      // alt="" (as rendered by snippets/product-finishing-display.liquid) and the name is read once
      if (primaryImg) {
        primaryImg.src = primary.thumb || '';
        primaryImg.alt = '';
        primaryImg.style.visibility = '';
      }
      if (accentImg) {
        if (hasAccent) {
          accentImg.src = accent.thumb || '';
          accentImg.alt = '';
          accentImg.style.visibility = '';
        } else {
          accentImg.src = '';
          accentImg.alt = '';
        }
      }

      card.dataset.hasAccent = hasAccent ? 'true' : 'false';
      if (wrap) wrap.dataset.hasAccent = hasAccent ? 'true' : 'false';

      card.dataset.finishNamePrimary = primary.name || '';
      card.dataset.finishLargePrimary = primary.large || primary.thumb || '';
      card.dataset.finishModalPrimary = primary.modal || primary.large || primary.thumb || '';
      if (hasAccent) {
        card.dataset.finishNameAccent = accent.name || '';
        card.dataset.finishLargeAccent = accent.large || accent.thumb || '';
        card.dataset.finishModalAccent = accent.modal || accent.large || accent.thumb || '';
      } else {
        delete card.dataset.finishNameAccent;
        delete card.dataset.finishLargeAccent;
        delete card.dataset.finishModalAccent;
      }
      var combinedName = primary.name + (hasAccent ? ' / ' + accent.name : '');
      card.dataset.finishName = combinedName;
      card.dataset.finishLarge = primary.large || primary.thumb || '';
      // Spec text for the popover (from the variant's primary JSON; empty on
      // accent-only promotion -> popover rows hide). Guards text-staleness on swap.
      card.dataset.finishToneLabel = primary.toneLabel || '';
      card.dataset.finishUsecase = primary.usecase || '';
      card.dataset.finishTexture = primary.texture || '';
      card.dataset.finishMaterial = primary.material || '';
      if (nameEl) {
        // Line-break after the "/" so the accent name wraps to its own line (matches
        // the server-rendered markup). Build via DOM nodes (not innerHTML) so finish
        // names stay text-safe; combinedName above stays plain for the dataset.
        // agfw: same node structure the Liquid emits, so the runtime
        // slash-reflow can measure this re-rendered card too.
        nameEl.textContent = '';
        nameEl.classList.remove('agfw-tight');
        if (hasAccent) {
          var agfwP = document.createElement('span');
          agfwP.className = 'agfw-p';
          agfwP.textContent = primary.name;
          var agfwS = document.createElement('span');
          agfwS.className = 'agfw-s';
          agfwS.textContent = ' /';
          var agfwBr = document.createElement('br');
          agfwBr.className = 'agfw-br';
          var agfwA = document.createElement('span');
          agfwA.className = 'agfw-a';
          agfwA.textContent = ' ' + accent.name;
          nameEl.appendChild(agfwP);
          nameEl.appendChild(agfwS);
          nameEl.appendChild(agfwBr);
          nameEl.appendChild(agfwA);
          if (window.AGFW) window.AGFW.reflow(nameEl);
        } else {
          nameEl.textContent = primary.name;
        }
      }
      card.setAttribute(
        'aria-label',
        primary.name + (hasAccent ? ' and ' + accent.name : '') + ' finishing — click to expand'
      );
    }

    // ---------- SELECT MODE: sync is-selected class to current variant ------

    function syncSelectedFromVariant(variantId) {
      if (variantId == null) return;
      var data = dataMap[String(variantId)];
      if (!data) return;
      var currentValue = data.finishingValue;
      if (currentValue == null) return;
      cards.forEach(function (card) {
        var match = card.dataset.variantValue === String(currentValue);
        card.classList.toggle('is-selected', match);
      });
    }

    function renderForVariant(variantId) {
      if (variantId == null) return;
      var data = dataMap[String(variantId)];
      if (!data) return;
      if (mode === 'select') {
        syncSelectedFromVariant(variantId);
      } else if (displayCard) {
        // DISPLAY mode = one card per product. Repaint primary + accent
        // from the variant's metaobject data; hide the card if primary
        // becomes incomplete.
        paintDisplayCard(displayCard, data.primary, data.accent);
      }
      // If this section's sheet is open, keep it in step with the variant.
      // repaintSheet no-ops when the key is unchanged (covers the radio
      // cascade's 120ms echo after an in-sheet select).
      if (sheetState && sheetState.ctx.sectionEl === sectionEl) {
        var src = null;
        if (mode === 'select') {
          src = ctx.cardByValue(data.finishingValue);
          if (sheetState.row) {
            // re-sync the row clones' rings to the page cards
            Array.prototype.forEach.call(
              sheetState.row.querySelectorAll('.ag-finish-card--selectable'),
              function (c) {
                var pageC = ctx.cardByValue(c.dataset.variantValue);
                c.classList.toggle('is-selected', !!(pageC && pageC.classList.contains('is-selected')));
              }
            );
          }
        } else if (displayCard && !displayCard.hidden) {
          // paintDisplayCard above already refreshed the card's data-attrs
          src = displayCard;
        }
        if (src) repaintSheet(src);
      }
    }

    function getCurrentVariantId() {
      var hidden = document.getElementById('product-variant-id');
      if (hidden && hidden.value) return hidden.value;
      var header = document.querySelector('[data-selected-variant]');
      if (header && header.dataset.selectedVariant) return header.dataset.selectedVariant;
      return null;
    }

    // ---------- Hover preview (both modes) ----------

    // Position the popover above the given card. Falls back below on top-clip.
    // Clamps left/right to viewport edges with 8px margin.
    function positionPreview(card) {
      if (!preview) return;
      var sectionRect = sectionEl.getBoundingClientRect();
      var wrap = card.querySelector('.ag-finish-card-img-wrap') || card;
      var cardRect = wrap.getBoundingClientRect();
      var pr = preview.getBoundingClientRect();
      var previewH = pr.height || 260;
      var previewW = pr.width || 580;

      var top = (cardRect.top - sectionRect.top) - previewH - 10;
      var left = (cardRect.left - sectionRect.left) + (cardRect.width / 2) - (previewW / 2);

      preview.style.top = top + 'px';
      preview.style.left = left + 'px';
      preview.style.right = 'auto';

      requestAnimationFrame(function () {
        var pr2 = preview.getBoundingClientRect();
        var vw = window.innerWidth;
        if (pr2.right > vw - 8) {
          preview.style.left = (parseFloat(preview.style.left) - (pr2.right - (vw - 8))) + 'px';
        }
        if (pr2.left < 8) {
          preview.style.left = (parseFloat(preview.style.left) + (8 - pr2.left)) + 'px';
        }
        if (pr2.top < 8) {
          preview.style.top = ((cardRect.bottom - sectionRect.top) + 10) + 'px';
        }
      });
    }

    // readPair + paintPanelContent live at module scope (shared with the sheet).

    function showPreview(card) {
      if (!preview || !previewPrimary) return;
      // Touch devices skip the preview.
      if (!hoverSupported) return;

      var pair = readPair(card);
      if (!pair.primaryLarge) return;

      // Same card as last rendered (keyed on the card, not just image src — two
      // finishes can share a base image but differ in text). Content already
      // correct, just show + reposition.
      if (preview.dataset.cardKey === pair.key) {
        preview.hidden = false;
        positionPreview(card);
        return;
      }

      // Decode-then-swap: stage the new primary (and accent if present) off-
      // screen, wait for it to be paint-ready, THEN commit the visible swap +
      // reveal. Prevents the previous finish from lingering during the new
      // image's network fetch on cold cache.
      var token = ++previewToken;
      var stagingP = new Image();
      if ('fetchPriority' in stagingP) stagingP.fetchPriority = 'high';
      stagingP.src = pair.primaryLarge;

      var stagingA = null;
      if (pair.hasAccent && pair.accentLarge) {
        stagingA = new Image();
        if ('fetchPriority' in stagingA) stagingA.fetchPriority = 'high';
        stagingA.src = pair.accentLarge;
      }

      var swapped = false;
      var swap = function () {
        if (swapped) return;
        if (token !== previewToken) return; // a newer hover took over
        swapped = true;
        // images + text land together via the shared painter (also feeds the
        // finish sheet's clone) — behavior identical to the pre-refactor swap.
        paintPanelContent(preview, pair);
        preview.dataset.cardKey = pair.key;
        preview.hidden = false;
        positionPreview(card);
      };

      var pending = stagingA ? 2 : 1;
      var settle = function () {
        pending -= 1;
        if (pending <= 0) swap();
      };

      var settleP = function () { settle(); };
      var settleA = function () { settle(); };

      if (stagingP.decode) {
        stagingP.decode().then(settleP, settleP);
      } else {
        stagingP.onload = settleP;
        stagingP.onerror = settleP;
      }
      if (stagingA) {
        if (stagingA.decode) {
          stagingA.decode().then(settleA, settleA);
        } else {
          stagingA.onload = settleA;
          stagingA.onerror = settleA;
        }
      }
      // Hard fallback — safety net for stuck/broken images. Extended from
      // 300ms (R13 initial) to 3000ms (R13.1) because the prior 300ms was
      // firing BEFORE decode completed on cold cache, revealing the popover
      // with stale or empty content (A's reported flash). 3s is generous
      // enough that decode/onload almost always wins; the timeout only
      // fires if the network truly stalls. Combined with the popover-URL =
      // thumb-URL change in the Liquid (browser cache serves popover
      // instantly), this should produce zero visible flash on cold or warm.
      setTimeout(swap, 3000);
    }

    function hidePreview() {
      if (preview) preview.hidden = true;
    }

    // ---------- Selectable click handler ----------

    function clickRadioForValue(value) {
      if (!optionName || value == null) return;
      var radios = document.querySelectorAll('input[type="radio"][name]');
      for (var i = 0; i < radios.length; i++) {
        var r = radios[i];
        if (r.name === optionName && r.value === value && !r.disabled) {
          r.click();
          return true;
        }
      }
      return false;
    }

    // ---------- Finish sheet triggers ----------
    // (openSheet/closeSheet live at module scope; the legacy .ag-finish-modal
    // code was removed 2026-07-17 — the sheet replaces it on all devices.)

    // DISPLAY-mode card: click/tap (any device) opens the sheet. Desktop gets
    // the centered presentation; ≤767px the bottom sheet.
    if (displayCard) {
      displayCard.addEventListener('click', function () {
        hidePreview();
        openSheet(ctx, displayCard);
      });
      displayCard.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          hidePreview();
          openSheet(ctx, displayCard);
        }
      });
    }

    // Hover-preview: bind to every .ag-finish-card (covers SELECT swatches +
    // DISPLAY card). Each carries data-finish-large-primary / -accent +
    // data-has-accent for the dual-mode preview.
    hoverables.forEach(function (el) {
      if (hoverSupported) {
        el.addEventListener('mouseenter', function () { showPreview(el); });
        el.addEventListener('mouseleave', hidePreview);
      }
      el.addEventListener('focus', function () {
        // closeSheet() restores focus to the trigger card — don't let that
        // synthetic focus pop the hover panel.
        if (suppressFocusPreview) return;
        showPreview(el);
      });
      el.addEventListener('blur', hidePreview);
    });

    cards.forEach(function (card) {
      if (mode === 'select' && card.classList.contains('ag-finish-card--selectable')) {
        card.addEventListener('click', function () {
          if (card.classList.contains('is-disabled')) return;
          // Optimistic .is-selected toggle so iOS sees the visual update
          // immediately (radio cascade can take 10–130ms).
          cards.forEach(function (c) { c.classList.toggle('is-selected', c === card); });
          hidePreview();
          var v = card.dataset.variantValue;
          clickRadioForValue(v);
          // Touch: the tap ALSO opens the finish sheet (A's directive
          // 2026-07-16 — select + see it larger in one tap). Desktop keeps
          // hover as the detail affordance; click stays select-only.
          if (!hoverSupported) openSheet(ctx, card);
        });
        card.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            card.click();
          }
        });
      }
    });

    // Variant-change re-render (covers radio + select inputs anywhere in form)
    document.querySelectorAll('.product-variant-option').forEach(function (input) {
      input.addEventListener('change', function () {
        setTimeout(function () {
          renderForVariant(getCurrentVariantId());
          hidePreview();
        }, 120);
      });
    });

    // Watch hidden variant ID for programmatic mutations.
    var hidden = document.getElementById('product-variant-id');
    if (hidden) {
      var lastSeen = hidden.value;
      var observer = new MutationObserver(function () {
        if (hidden.value !== lastSeen) {
          lastSeen = hidden.value;
          renderForVariant(lastSeen);
          hidePreview();
        }
      });
      observer.observe(hidden, { attributes: true, attributeFilter: ['value'] });
      hidden.addEventListener('change', function () {
        renderForVariant(hidden.value);
      });
    }

    renderForVariant(getCurrentVariantId());
  }

  function init() {
    document.querySelectorAll('[data-finishing-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ---- agfw (2026-08-22, A's flag): finish-card caption slash reflow --------- */
(function () {
  'use strict';
  function reflow(el) {
    if (!el) return;
    var p = el.querySelector('.agfw-p');
    var s = el.querySelector('.agfw-s');
    var br = el.querySelector('.agfw-br');
    if (!p || !s || !br) return;              // no accent -> nothing to balance
    // Always measure the NATURAL layout. With the break already suppressed the
    // slash can never look orphaned, so a stale decision would feed itself and
    // the class could never clear.
    el.classList.remove('agfw-tight');
    // Mobile keeps its single-line nowrap + fade register. Read the computed
    // value instead of restating the breakpoint here.
    if (window.getComputedStyle(el).whiteSpace === 'nowrap') return;
    var rects = p.getClientRects();
    if (!rects.length) return;                // not rendered (hidden card)
    var last = rects[rects.length - 1];       // the primary's LAST line box
    var sr = s.getBoundingClientRect();
    if (!sr.width && !sr.height) return;
    if (sr.top - last.top > 1) el.classList.add('agfw-tight');  // sub-pixel slack
  }
  function all(root) {
    var els = (root || document).querySelectorAll('.ag-finish-card-name');
    for (var i = 0; i < els.length; i++) reflow(els[i]);
  }
  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; all(); });
  }
  window.AGFW = { reflow: reflow, all: all, schedule: schedule };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
  // Alata swapping in changes every width: a decision made in the fallback face
  // would otherwise stick forever.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  window.addEventListener('resize', schedule);
})();
